import { Controller } from "@hotwired/stimulus"

import { trackPlay } from "@/lib/ahoy"
import { trackContentPlay } from "@/lib/posthog"
import { continuousPlayEnabled, formatTime } from "@/lib/utils"

import type ImmersivePlayerController from "./immersive_player_controller"

// Reported by the native player through the audio bridge (#1460).
export interface NativeState {
  state: "playing" | "paused" | "stalled" | "ended" | "failed"
  itemId: string
  position: number
}

interface QueueItem {
  itemId: string
  containerId?: string | null
  title: string
  creatorName: string
  durationSeconds: number
  muxPlaybackId: string | null
  imageUrl: string
  spaceId?: string
  spaceUrl?: string
}

interface QueueOutlet {
  next: () => void
  previous: (currentTime: number) => void
  advance: () => void
  skipTo: (index: number) => void
  clearUpcoming: () => void
  skipUnplayable: () => void
  savePosition: (currentTime: number) => void
  requestCurrentToken: () => void
  restoredTime: number
  currentItem: QueueItem | undefined
  currentIndex: number
  items: QueueItem[]
  upNextItems: QueueItem[]
  hasNext: boolean
  hasPrevious: boolean
}

const POSITION_THROTTLE_MS = 5000

export default class extends Controller {
  static targets = [
    "audioTemplate",
    "fileAudioTemplate",
    "title",
    "creator",
    "artwork",
    "nextButton",
    "previousButton",
    "miniElapsed",
    "miniDuration",
    "playPauseButton",
    "playPauseIcon",
    "progressLine",
    "nowPlaying",
    "npTitle",
    "npCreator",
    "npArtwork",
    "npNextButton",
    "npPreviousButton",
    "npPlayPauseButton",
    "npPlayPauseIcon",
    "progressBar",
    "progressFill",
    "currentTime",
    "totalDuration",
    "upNext",
    "upNextList",
    "upNextItemTemplate",
    "npArtworkPlaceholder",
  ]

  static outlets = ["playback-queue", "immersive-player", "bridge-player"]

  // Server-rendered truth (the client registered the player bridge
  // component) — unlike bridgeActive it needs no outlet, so it can't
  // race outlet-connection order on a fresh page load.
  static values = { nativePlayer: Boolean }

  declare readonly nativePlayerValue: boolean

  declare readonly audioTemplateTarget: HTMLTemplateElement
  declare readonly fileAudioTemplateTarget: HTMLTemplateElement
  declare readonly hasFileAudioTemplateTarget: boolean
  declare readonly titleTarget: HTMLElement
  declare readonly creatorTarget: HTMLElement
  declare readonly artworkTarget: HTMLImageElement
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly previousButtonTarget: HTMLButtonElement
  declare readonly hasNextButtonTarget: boolean
  declare readonly hasPreviousButtonTarget: boolean
  declare readonly miniElapsedTarget: HTMLElement
  declare readonly miniDurationTarget: HTMLElement
  declare readonly hasMiniElapsedTarget: boolean
  declare readonly hasMiniDurationTarget: boolean
  declare readonly playPauseButtonTarget: HTMLButtonElement
  declare readonly playPauseIconTarget: HTMLElement
  declare readonly progressLineTarget: HTMLElement
  declare readonly nowPlayingTarget: HTMLElement
  declare readonly npTitleTarget: HTMLElement
  declare readonly npCreatorTarget: HTMLElement
  declare readonly npArtworkTarget: HTMLImageElement
  declare readonly npNextButtonTarget: HTMLButtonElement
  declare readonly npPreviousButtonTarget: HTMLButtonElement
  declare readonly npPlayPauseButtonTarget: HTMLButtonElement
  declare readonly npPlayPauseIconTarget: HTMLElement
  declare readonly progressBarTarget: HTMLElement
  declare readonly progressFillTarget: HTMLElement
  declare readonly currentTimeTarget: HTMLElement
  declare readonly totalDurationTarget: HTMLElement
  declare readonly upNextTarget: HTMLElement
  declare readonly upNextListTarget: HTMLElement
  declare readonly hasAudioTemplateTarget: boolean
  declare readonly hasNowPlayingTarget: boolean
  declare readonly hasProgressLineTarget: boolean
  declare readonly hasProgressBarTarget: boolean
  declare readonly hasUpNextListTarget: boolean
  declare readonly upNextItemTemplateTarget: HTMLTemplateElement
  declare readonly hasUpNextItemTemplateTarget: boolean
  declare readonly npArtworkPlaceholderTarget: HTMLElement
  declare readonly hasNpArtworkPlaceholderTarget: boolean
  declare readonly playbackQueueOutlet: QueueOutlet
  declare readonly immersivePlayerOutlet: ImmersivePlayerController
  declare readonly hasImmersivePlayerOutlet: boolean

  private positionTimer: ReturnType<typeof setInterval> | null = null
  private trackedPlays = new Set<string>()
  private endedHandler: (() => void) | null = null
  private timeUpdateHandler: (() => void) | null = null
  private playHandler: (() => void) | null = null
  private pauseHandler: (() => void) | null = null
  private currentDuration = 0

  declare readonly bridgePlayerOutlet: {
    enabled: boolean
    play(itemId: string): void
    pause(): void
    seek(position: number): void
    sendQueue(): void
  }
  declare readonly hasBridgePlayerOutlet: boolean

  private nativeState: NativeState | null = null
  // Saved listening position from a restored queue — applied once when
  // the engine mounts on the first play intent.
  private pendingRestoreTime = 0

  // Inside the native app the bridge commands the native engine; the web
  // engine never mounts. In a plain browser the bridge is absent/inert
  // and everything below behaves exactly as before.
  private get bridgeActive(): boolean {
    return this.hasBridgePlayerOutlet && this.bridgePlayerOutlet.enabled
  }

  connect() {
    // A surviving engine (navigation transplant) needs its listeners and
    // icon re-synced — see wireAudioListeners.
    const engine = this.findAudioElement()
    if (engine) {
      this.wireAudioListeners(engine)
      this.updatePlayPauseIcon(!engine.paused && !engine.ended)
      this.startPositionTimer()
    }
    // Hardware media keys / OS media controls drive the global player.
    if ("mediaSession" in navigator) {
      // play/pause are explicit requested states — toggling would invert
      // the command when the browser re-dispatches the current state.
      navigator.mediaSession.setActionHandler("play", () => this.playAudio())
      navigator.mediaSession.setActionHandler("pause", () => this.pauseAudio())
      navigator.mediaSession.setActionHandler("nexttrack", () => this.next())
      navigator.mediaSession.setActionHandler("previoustrack", () =>
        this.previous(),
      )
    }
  }

  disconnect() {
    this.clearPositionTimer()
    // Deliberately NOT removing audio listeners: on a Turbo permanent
    // transplant this disconnect can be processed AFTER the reconnect,
    // which would strip the listeners connect() just re-wired and leave
    // the UI deaf (frozen progress, unresponsive controls). The same
    // controller instance survives the transplant, and wireAudioListeners
    // removes-before-adding, so nothing leaks.
    if ("mediaSession" in navigator) {
      navigator.mediaSession.setActionHandler("play", null)
      navigator.mediaSession.setActionHandler("pause", null)
      navigator.mediaSession.setActionHandler("nexttrack", null)
      navigator.mediaSession.setActionHandler("previoustrack", null)
    }
  }

  handleChange(event: CustomEvent) {
    const detail = event.detail as {
      item?: QueueItem
      playIntent?: boolean
    } | null
    const item = detail?.item
    if (!item) return

    this.show()
    this.updateTrackInfo(item)
    this.updateTransportButtons()
    this.renderUpNext()
    if (detail?.playIntent === false) return

    // A fresh enqueue starts from the top — drop any restored position.
    this.pendingRestoreTime = 0
    // Optimistic: enqueueing IS play intent. The token fetch + engine
    // boot take real time; flip the bar now, real play/pause events
    // correct it if anything fails.
    this.updatePlayPauseIcon(true)
  }

  // On a fresh page load the queue controller connects (and dispatches
  // restore) before this element's document listener exists — the event
  // is unreliable there. The outlet-connected callback is the pull-based
  // complement: sync from the queue whenever it appears.
  playbackQueueOutletConnected() {
    const item = this.playbackQueueOutlet.currentItem
    if (!item) return

    this.show()
    this.updateTrackInfo(item)
    this.updateTransportButtons()
    this.renderUpNext()
    this.pendingRestoreTime = this.playbackQueueOutlet.restoredTime
    const audio = this.findAudioElement()
    this.updatePlayPauseIcon(Boolean(audio && !audio.paused && !audio.ended))
  }

  handleRestore(event: CustomEvent) {
    const { item, currentTime } = event.detail as {
      item: QueueItem | undefined
      currentTime: number
    }
    if (!item) return
    this.pendingRestoreTime = currentTime || 0

    this.show()
    this.updateTrackInfo(item)
    this.updateTransportButtons()
    this.renderUpNext()
    // A restore is NOT play intent — a fresh page with a saved queue has
    // no engine yet. Reflect the actual audio state (which also keeps
    // the pause icon during in-flight Turbo navigation while playing).
    const audio = this.findAudioElement()
    this.updatePlayPauseIcon(Boolean(audio && !audio.paused && !audio.ended))
  }

  handleToken(event: CustomEvent) {
    // The token is a web-engine concern; native was commanded on the change.
    if (this.bridgeActive) return

    const { playbackId, token, fileUrl } = event.detail as {
      playbackId: string | null
      token: string | null
      fileUrl?: string | null
    }

    // Queue controllers are per-page and re-hydrate on every Turbo visit,
    // re-requesting a token for the track that is already mounted in this
    // permanent element. Rebuilding would restart (and briefly double)
    // playback — leave a live element for the same track alone.
    const existing = this.findAudioElement()
    const sameSource =
      existing &&
      !existing.ended &&
      (playbackId
        ? existing.getAttribute("playback-id") === playbackId
        : Boolean(fileUrl) &&
          existing.getAttribute("data-file-src") === fileUrl)
    if (existing && sameSource) {
      // A play affordance re-selected the mounted track: a paused engine
      // must resume, not silently no-op (the "dead click").
      if (existing.paused) void existing.play()
      this.startPositionTimer()
      return
    }

    this.mountAudio(playbackId, token, fileUrl ?? null)
    if (this.pendingRestoreTime > 0) {
      // Seek only once metadata exists — seeking a Mux stream at
      // readyState 0 wedges it (observed live: frozen at the restored
      // position, no segments ever fetched).
      const mounted = this.findAudioElement()
      const resumeAt = this.pendingRestoreTime
      if (mounted) {
        mounted.addEventListener(
          "loadedmetadata",
          () => {
            mounted.currentTime = resumeAt
          },
          { once: true },
        )
      }
      this.pendingRestoreTime = 0
    }
    this.startPositionTimer()
  }

  handleEnded() {
    this.clearPositionTimer()
  }

  handleRestart() {
    const audio = this.findAudioElement()
    if (audio) audio.currentTime = 0
  }

  next() {
    this.playbackQueueOutlet.next()
  }

  previous() {
    const audio = this.findAudioElement()
    const currentTime = audio?.currentTime ?? 0
    this.playbackQueueOutlet.previous(currentTime)
  }

  private playAudio() {
    const audio = this.findAudioElement()
    if (audio?.paused) void audio.play()
  }

  private pauseAudio() {
    const audio = this.findAudioElement()
    if (audio && !audio.paused) audio.pause()
  }

  togglePlay() {
    if (this.bridgeActive) {
      const item = this.playbackQueueOutlet.currentItem
      if (!item) return
      if (this.nativeState?.state === "playing") {
        this.bridgePlayerOutlet.pause()
      } else {
        this.bridgePlayerOutlet.play(item.itemId)
      }
      return
    }

    const audio = this.findAudioElement()
    if (!audio) {
      // A restored queue after a full reload has no engine yet — the
      // first play intent fetches a token and mounts it (autoplay), and
      // handleToken resumes at the restored position.
      if (this.playbackQueueOutlet.currentItem) {
        this.playbackQueueOutlet.requestCurrentToken()
      }
      return
    }

    if (audio.paused) {
      void audio.play()
    } else {
      audio.pause()
    }
  }

  // Bridge callbacks: the native engine is the source of truth while the
  // bridge is active; the web UI mirrors it.
  applyNativeState(state: NativeState) {
    this.nativeState = state
    const playing = state.state === "playing"
    this.updatePlayPauseIcon(playing)
    this.dispatch(playing ? "play" : "pause")
  }

  updateNativePosition(position: number) {
    if (this.nativeState?.state !== "playing") return
    const item = this.playbackQueueOutlet.currentItem
    const duration = item?.durationSeconds ?? 0
    if (duration <= 0) return

    const ratio = position / duration
    if (this.hasProgressLineTarget)
      this.progressLineTarget.style.width = `${ratio * 100}%`
    if (this.hasMiniElapsedTarget)
      this.miniElapsedTarget.textContent = formatTime(position)
    if (item) {
      this.dispatch("progress", {
        detail: { itemId: item.itemId, currentTime: position, duration },
      })
    }
  }

  expand() {
    if (!this.hasNowPlayingTarget) return
    // Nothing has played yet (root still hidden) — the Now Playing tab
    // should be inert rather than open an empty overlay.
    if (this.element.classList.contains("hidden")) return
    this.nowPlayingTarget.style.display = ""
  }

  collapse() {
    if (!this.hasNowPlayingTarget) return
    this.nowPlayingTarget.style.display = "none"
  }

  seek(event: MouseEvent) {
    const audio = this.findAudioElement()
    if (!audio?.duration) return

    const bar = event.currentTarget as HTMLElement
    const rect = bar.getBoundingClientRect()
    if (!rect.width) return
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - rect.left) / rect.width),
    )
    audio.currentTime = ratio * audio.duration
  }

  skipToTrack(event: Event) {
    const target = event.currentTarget as HTMLElement
    const index = target.dataset.queueIndex
    if (index != null) {
      this.playbackQueueOutlet.skipTo(parseInt(index, 10))
    }
  }

  clearQueue() {
    this.playbackQueueOutlet.clearUpcoming()
    this.renderUpNext()
  }

  enterImmersive() {
    const im = this.immersive
    if (!im) return
    const item = this.playbackQueueOutlet.currentItem
    if (!item) return
    const q = this.playbackQueueOutlet
    const audio = this.findAudioElement()
    if (this.hasNowPlayingTarget) this.nowPlayingTarget.style.display = "none"
    im.enter(item, q.hasPrevious, q.hasNext, audio ? !audio.paused : false)
  }

  exitImmersive() {
    if (!this.immersive?.isActive) return
    this.immersive.exit()
    if (this.hasNowPlayingTarget) this.nowPlayingTarget.style.display = ""
  }

  // --- private ---

  private show() {
    // Native renders its own mini-player bar above the web view (#1547);
    // unhiding the web bar would stack two players. State keeps flowing
    // into the hidden UI so nothing else special-cases native.
    if (this.nativePlayerValue) return
    this.element.classList.remove("hidden")
  }

  // First-play analytics parity with the retired page-embedded players
  // (PostHog + Ahoy), once per item per session.
  private trackFirstPlay() {
    const item = this.playbackQueueOutlet.currentItem
    if (!item || this.trackedPlays.has(item.itemId)) return
    this.trackedPlays.add(item.itemId)

    trackContentPlay(
      item.itemId,
      item.title,
      item.spaceId ?? "",
      item.spaceUrl ?? "",
      "audio",
      // Matches the trackPlay call below. Omitting it here shipped
      // content_play with container attribution permanently absent on the
      // main listener player, which is most of the playback we measure.
      item.containerId ?? undefined,
    )
    trackPlay(
      item.itemId,
      item.spaceId ?? "",
      item.durationSeconds,
      undefined,
      "web",
      "stream",
      item.containerId ?? undefined,
    )
  }

  private get immersive(): ImmersivePlayerController | null {
    return this.hasImmersivePlayerOutlet ? this.immersivePlayerOutlet : null
  }

  private updateTrackInfo(item: QueueItem) {
    this.titleTarget.textContent = item.title
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: item.title,
        artist: item.creatorName,
        artwork: item.imageUrl ? [{ src: item.imageUrl }] : [],
      })
    }
    this.creatorTarget.textContent = item.creatorName
    this.currentDuration = item.durationSeconds

    if (item.imageUrl) {
      this.artworkTarget.src = item.imageUrl
      this.artworkTarget.classList.remove("hidden")
    } else {
      this.artworkTarget.classList.add("hidden")
    }

    if (this.hasNowPlayingTarget) {
      this.npTitleTarget.textContent = item.title
      this.npCreatorTarget.textContent = item.creatorName
      this.totalDurationTarget.textContent = formatTime(item.durationSeconds)
      this.currentTimeTarget.textContent = "0:00"
      if (this.hasMiniDurationTarget)
        this.miniDurationTarget.textContent = formatTime(item.durationSeconds)
      if (this.hasMiniElapsedTarget) this.miniElapsedTarget.textContent = "0:00"

      if (item.imageUrl) {
        this.npArtworkTarget.src = item.imageUrl
        this.npArtworkTarget.classList.remove("hidden")
        if (this.hasNpArtworkPlaceholderTarget)
          this.npArtworkPlaceholderTarget.classList.add("hidden")
      } else {
        this.npArtworkTarget.classList.add("hidden")
        if (this.hasNpArtworkPlaceholderTarget)
          this.npArtworkPlaceholderTarget.classList.remove("hidden")
      }
    }

    if (this.immersive?.isActive) this.immersive.syncTrackInfo(item)
  }

  private updateTransportButtons() {
    const q = this.playbackQueueOutlet
    if (this.hasPreviousButtonTarget)
      this.previousButtonTarget.disabled = !q.hasPrevious
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = !q.hasNext

    if (this.hasNowPlayingTarget) {
      this.npPreviousButtonTarget.disabled = !q.hasPrevious
      this.npNextButtonTarget.disabled = !q.hasNext
    }

    if (this.immersive?.isActive)
      this.immersive.syncTransport(q.hasPrevious, q.hasNext)
  }

  private renderUpNext() {
    if (!this.hasUpNextListTarget) return
    const upNext = this.playbackQueueOutlet.upNextItems
    const baseIndex = this.playbackQueueOutlet.currentIndex + 1

    this.upNextListTarget.innerHTML = ""
    upNext.forEach((item, i) => {
      const li = this.buildUpNextItem(item, baseIndex + i)
      this.upNextListTarget.appendChild(li)
    })

    if (upNext.length === 0) {
      this.upNextTarget.classList.add("hidden")
    } else {
      this.upNextTarget.classList.remove("hidden")
    }
  }

  private buildUpNextItem(item: QueueItem, index: number): HTMLElement {
    const clone = this.upNextItemTemplateTarget.content.cloneNode(
      true,
    ) as DocumentFragment
    const li = clone.firstElementChild as HTMLElement
    li.dataset.queueIndex = String(index)

    const titleEl = li.querySelector<HTMLElement>("[data-slot='title']")
    const creatorEl = li.querySelector<HTMLElement>("[data-slot='creator']")
    const durationEl = li.querySelector<HTMLElement>("[data-slot='duration']")
    if (titleEl) titleEl.textContent = item.title
    if (creatorEl) creatorEl.textContent = item.creatorName
    if (durationEl) durationEl.textContent = formatTime(item.durationSeconds)

    return li
  }

  // Engine event wiring, shared by mountAudio and reconnect: the bar is
  // data-turbo-permanent, so Stimulus disconnects this controller on
  // every navigation — disconnect() removes these listeners while the
  // out-of-body engine plays on. Without re-attaching on connect, the
  // UI goes deaf after any navigation: frozen progress, icons that no
  // longer reflect play/pause (the "click twice" feel), and no
  // ended→advance.
  private wireAudioListeners(el: HTMLElement) {
    this.removeAudioListeners()
    this.endedHandler = () => this.onTrackEnded()
    this.timeUpdateHandler = () => this.onTimeUpdate()
    this.playHandler = () => {
      this.updatePlayPauseIcon(true)
      this.dispatch("play")
      this.trackFirstPlay()
    }
    this.pauseHandler = () => {
      this.updatePlayPauseIcon(false)
      this.dispatch("pause")
    }

    el.addEventListener("ended", this.endedHandler)
    el.addEventListener("timeupdate", this.timeUpdateHandler)
    el.addEventListener("play", this.playHandler)
    el.addEventListener("pause", this.pauseHandler)
  }

  private mountAudio(
    playbackId: string | null,
    token: string | null,
    fileUrl: string | null = null,
  ) {
    if (!playbackId && !fileUrl) return

    const container = this.audioHost()
    container.innerHTML = ""

    // Both engines clone from inert <template> blueprints in the bar
    // markup — never built inline.
    let muxAudio: HTMLElement
    if (playbackId) {
      if (!this.hasAudioTemplateTarget) return
      const clone = this.audioTemplateTarget.content.cloneNode(
        true,
      ) as DocumentFragment
      muxAudio = clone.firstElementChild as HTMLElement
      muxAudio.setAttribute("playback-id", playbackId)
      if (token) muxAudio.setAttribute("playback-token", token)
    } else {
      // File-backed fallback: a plain <audio> on the attachment URL,
      // wired identically to the mux engine.
      if (!this.hasFileAudioTemplateTarget) return
      const clone = this.fileAudioTemplateTarget.content.cloneNode(
        true,
      ) as DocumentFragment
      muxAudio = clone.firstElementChild as HTMLElement
      muxAudio.setAttribute("src", fileUrl!)
      muxAudio.setAttribute("data-file-src", fileUrl!)
    }
    muxAudio.setAttribute("autoplay", "")
    container.appendChild(muxAudio)

    this.wireAudioListeners(muxAudio)

    this.updatePlayPauseIcon(true)
  }

  private onTrackEnded() {
    this.clearPositionTimer()
    // Container rules (QA #2): auto-advance only with continuous play on.
    // Same localStorage key as the container page toggle — one preference.
    if (continuousPlayEnabled()) {
      this.playbackQueueOutlet.advance()
    } else {
      this.updatePlayPauseIcon(false)
      this.dispatch("pause")
    }
  }

  private onTimeUpdate() {
    const audio = this.findAudioElement()
    if (!audio) return

    const current = audio.currentTime
    const duration = audio.duration || this.currentDuration || 1
    const ratio = duration > 0 ? current / duration : 0

    if (this.hasProgressLineTarget) {
      this.progressLineTarget.style.width = `${ratio * 100}%`
    }
    if (this.hasMiniElapsedTarget)
      this.miniElapsedTarget.textContent = formatTime(current)
    if (this.hasMiniDurationTarget && audio.duration)
      this.miniDurationTarget.textContent = formatTime(audio.duration)

    if (this.hasProgressBarTarget) {
      this.progressFillTarget.style.width = `${ratio * 100}%`
      this.currentTimeTarget.textContent = formatTime(current)
      if (audio.duration) {
        this.totalDurationTarget.textContent = formatTime(audio.duration)
      }
    }

    if (this.immersive?.isActive)
      this.immersive.updateProgress(ratio, current, audio.duration)

    // Page-level displays (item-page inline player, container-row
    // progress rings) mirror the one global engine via this broadcast.
    const item = this.playbackQueueOutlet.currentItem
    if (item) {
      this.dispatch("progress", {
        detail: { itemId: item.itemId, currentTime: current, duration },
      })
    }
  }

  // Seek requests from page-level displays (track-progress controllers).
  // Only honored while the requesting item is the current track — the
  // page display is a mirror, not a second player.
  handleSeek(event: CustomEvent<{ itemId: string; ratio: number }>) {
    if (this.bridgeActive) {
      const item = this.playbackQueueOutlet.currentItem
      if (item?.itemId !== event.detail.itemId) return
      const duration = item.durationSeconds
      if (!duration) return
      this.bridgePlayerOutlet.seek(
        Math.max(0, Math.min(1, event.detail.ratio)) * duration,
      )
      return
    }

    const audio = this.findAudioElement()
    const item = this.playbackQueueOutlet.currentItem
    if (!audio || item?.itemId !== event.detail.itemId) return

    const duration = audio.duration || this.currentDuration
    if (!duration) return
    audio.currentTime = Math.max(0, Math.min(1, event.detail.ratio)) * duration
  }

  private updatePlayPauseIcon(playing: boolean) {
    const name = playing ? "pause" : "play"
    this.playPauseIconTarget.setAttribute("name", name)
    // Snapshot for tracklists that connect after playback started (page
    // navigation while playing) — events alone can't reach them.
    ;(this.element as HTMLElement).dataset.playing = playing ? "true" : "false"

    if (this.hasNowPlayingTarget) {
      this.npPlayPauseIconTarget.setAttribute("name", name)
    }

    if (this.immersive?.isActive) this.immersive.updatePlayPauseIcon(playing)
  }

  // The audio engine lives OUTSIDE <body>, directly on <html>. Turbo only
  // swaps and snapshots the body, so audio hosted here survives every
  // navigation untouched — no pauses from permanence transplants, no
  // autoplaying clones from cached snapshots. The bar is just UI.
  //
  // The host must be created here rather than server-rendered: the HTML
  // parser only allows <head>/<body> as <html> children and hoists
  // anything else into <body> — inside Turbo's reach. JS insertion after
  // parse is the only way an element can live outside <body>.
  private audioHost(): HTMLElement {
    let host = document.getElementById("indee-audio-host")
    if (!host) {
      host = document.createElement("div")
      host.id = "indee-audio-host"
      host.style.display = "none"
      document.documentElement.appendChild(host)
    }
    return host
  }

  private findAudioElement(): HTMLMediaElement | null {
    const host = document.getElementById("indee-audio-host")
    if (!host) return null
    const mux = host.querySelector<HTMLMediaElement>("mux-audio")
    return mux ?? host.querySelector<HTMLMediaElement>("audio")
  }

  private removeAudioListeners() {
    const el = this.findAudioElement()
    if (el) {
      if (this.endedHandler) el.removeEventListener("ended", this.endedHandler)
      if (this.timeUpdateHandler)
        el.removeEventListener("timeupdate", this.timeUpdateHandler)
      if (this.playHandler) el.removeEventListener("play", this.playHandler)
      if (this.pauseHandler) el.removeEventListener("pause", this.pauseHandler)
    }
    this.endedHandler = null
    this.timeUpdateHandler = null
    this.playHandler = null
    this.pauseHandler = null
  }

  private startPositionTimer() {
    this.clearPositionTimer()
    this.positionTimer = setInterval(() => {
      const audio = this.findAudioElement()
      if (audio && "currentTime" in audio) {
        this.playbackQueueOutlet.savePosition(audio.currentTime)
      }
    }, POSITION_THROTTLE_MS)
  }

  private clearPositionTimer() {
    if (this.positionTimer) {
      clearInterval(this.positionTimer)
      this.positionTimer = null
    }
  }
}
